/**
 * Lightbox de diagrama: abre um overlay em tela cheia com zoom (scroll do
 * mouse, centrado no cursor) e pan (clicar e arrastar).
 *
 * DOM puro (sem estado React) porque a mesma interação precisa existir também
 * no HTML exportado (arquivo estático, sem bundle) - ver EXPORT_LIGHTBOX_CSS /
 * EXPORT_LIGHTBOX_SCRIPT em exportConversation.tsx, que replica este algoritmo
 * em JS plano embutido no `<script>` do documento exportado.
 */

const MIN_SCALE = 0.5;
const MAX_SCALE = 8;
const ZOOM_SPEED = 0.0015;

export function openDiagramLightbox(contentHtml: string): void {
  const overlay = document.createElement("div");
  overlay.className = "dlb-overlay";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "dlb-close";
  closeBtn.setAttribute("aria-label", "Fechar");
  closeBtn.textContent = "×";

  const viewport = document.createElement("div");
  viewport.className = "dlb-viewport";

  const content = document.createElement("div");
  content.className = "dlb-content";
  content.innerHTML = contentHtml;

  // O SVG do mermaid usa width="100%" (relativo ao container) - na bolha do
  // chat isso funciona porque o container tem largura própria, mas aqui
  // `.dlb-content` se auto-dimensiona pelo conteúdo (shrink-to-fit): 100% de
  // um container sem largura própria vira uma referência circular e o
  // navegador cai no tamanho padrão de replaced element (~300px). Fixamos
  // width/height explícitos a partir do viewBox pra ter um tamanho natural
  // determinístico, e deixamos o zoom (scale) cuidar de ampliar/reduzir.
  const svg = content.querySelector("svg");
  if (svg) {
    const vb = svg.getAttribute("viewBox");
    const dims = vb?.split(/[\s,]+/).map(Number);
    const w = dims?.[2];
    const h = dims?.[3];
    if (w && h && w > 0 && h > 0) {
      svg.setAttribute("width", String(w));
      svg.setAttribute("height", String(h));
    }
    svg.style.maxWidth = "none";
  }

  viewport.appendChild(content);
  overlay.appendChild(closeBtn);
  overlay.appendChild(viewport);
  document.body.appendChild(overlay);
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  let scale = 1;
  let tx = 0;
  let ty = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let moved = false;

  const apply = () => {
    content.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const rect = viewport.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = Math.exp(-e.deltaY * ZOOM_SPEED);
    const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * factor));
    tx = cx - ((cx - tx) * newScale) / scale;
    ty = cy - ((cy - ty) * newScale) / scale;
    scale = newScale;
    apply();
  };

  const onPointerDown = (e: PointerEvent) => {
    dragging = true;
    moved = false;
    lastX = e.clientX;
    lastY = e.clientY;
    viewport.classList.add("dlb-grabbing");
    viewport.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) return;
    tx += e.clientX - lastX;
    ty += e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    moved = true;
    apply();
  };
  const onPointerUp = (e: PointerEvent) => {
    dragging = false;
    viewport.classList.remove("dlb-grabbing");
    try {
      viewport.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const close = () => {
    document.removeEventListener("keydown", onKeydown);
    document.body.style.overflow = prevOverflow;
    overlay.remove();
  };
  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };

  overlay.addEventListener("click", (e) => {
    // viewport ocupa toda a área (flex:1) - clicar fora do conteúdo cai nele,
    // não no overlay (que só "aparece" atrás do close-btn fixed). Fecha se o
    // alvo for o fundo (overlay/viewport), não o diagrama em si.
    if ((e.target === overlay || e.target === viewport) && !moved) close();
  });
  closeBtn.addEventListener("click", close);
  viewport.addEventListener("wheel", onWheel, { passive: false });
  viewport.addEventListener("pointerdown", onPointerDown);
  viewport.addEventListener("pointermove", onPointerMove);
  viewport.addEventListener("pointerup", onPointerUp);
  viewport.addEventListener("pointerleave", onPointerUp);
  document.addEventListener("keydown", onKeydown);

  // ajusta a escala inicial pra caber no viewport (nunca amplia além do
  // tamanho natural) e centraliza, depois que o layout do conteúdo assentou.
  requestAnimationFrame(() => {
    const vRect = viewport.getBoundingClientRect();
    const cRect = content.getBoundingClientRect();
    const fit = Math.min(1, (vRect.width * 0.92) / cRect.width, (vRect.height * 0.92) / cRect.height);
    scale = Math.max(MIN_SCALE, fit);
    tx = (vRect.width - cRect.width * scale) / 2;
    ty = (vRect.height - cRect.height * scale) / 2;
    apply();
  });
}
